import type { MedplumClient } from '@medplum/core'
import type { Practitioner } from '@medplum/fhirtypes'

const BASE_EXTENSION_URL =
  'https://behuman.health/fhir/StructureDefinition/practitioner-custom-test-panels'

/** @deprecated Use vendor-specific URL via `extensionUrl()` instead. */
export const CUSTOM_PANELS_EXTENSION_URL = BASE_EXTENSION_URL

export const PANEL_VENDOR_LABCORP = 'labcorp'
export const PANEL_VENDOR_ACCUREFERENCE = 'accureference'
// Quest has no personal-panel UI; this identifies Quest's SHARED (practice-wide)
// panels, which are org-owned and read from middleware, not this file.
export const PANEL_VENDOR_QUEST = 'quest'

function extensionUrl(vendor: string): string {
  return `${BASE_EXTENSION_URL}-${vendor}`
}

export interface CustomTestPanel {
  id: string
  name: string
  testCodes: string[]
  vendor?: string
  createdAt: string
  updatedAt: string
}

export function loadCustomPanels(
  _medplum: MedplumClient,
  practitioner: Practitioner,
  vendor?: string,
): CustomTestPanel[] {
  const url = vendor ? extensionUrl(vendor) : BASE_EXTENSION_URL
  const ext = practitioner.extension?.find((e) => e.url === url)
  if (!ext?.valueString) return []
  try {
    const panels = JSON.parse(ext.valueString) as CustomTestPanel[]
    if (vendor) {
      return panels.filter((p) => !p.vendor || p.vendor === vendor)
    }
    return panels
  } catch (err) {
    console.warn('[CustomPanels] Failed to parse panels:', err)
    return []
  }
}

async function savePanels(
  medplum: MedplumClient,
  practitioner: Practitioner,
  panels: CustomTestPanel[],
  vendor?: string,
): Promise<void> {
  const url = vendor ? extensionUrl(vendor) : BASE_EXTENSION_URL
  const existingExtensions = (practitioner.extension || []).filter((ext) => ext.url !== url)
  await medplum.updateResource<Practitioner>({
    ...practitioner,
    extension: [
      ...existingExtensions,
      {
        url,
        valueString: JSON.stringify(panels),
      },
    ],
  })
}

export async function createCustomPanel(
  medplum: MedplumClient,
  practitionerId: string,
  name: string,
  testCodes: string[],
  vendor?: string,
): Promise<CustomTestPanel[]> {
  const practitioner = await medplum.readResource('Practitioner', practitionerId)
  const allPanels = loadCustomPanels(medplum, practitioner, vendor)
  const now = new Date().toISOString()
  const newPanel: CustomTestPanel = {
    id: crypto.randomUUID(),
    name,
    testCodes,
    vendor,
    createdAt: now,
    updatedAt: now,
  }
  const updated = [...allPanels, newPanel]
  await savePanels(medplum, practitioner, updated, vendor)
  return vendor ? updated.filter((p) => !p.vendor || p.vendor === vendor) : updated
}

export async function updateCustomPanel(
  medplum: MedplumClient,
  practitionerId: string,
  panelId: string,
  updates: { name?: string; testCodes?: string[] },
  vendor?: string,
): Promise<CustomTestPanel[]> {
  const practitioner = await medplum.readResource('Practitioner', practitionerId)
  const allPanels = loadCustomPanels(medplum, practitioner, vendor)
  const updated = allPanels.map((p) =>
    p.id === panelId ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p,
  )
  await savePanels(medplum, practitioner, updated, vendor)
  return vendor ? updated.filter((p) => !p.vendor || p.vendor === vendor) : updated
}

export async function deleteCustomPanel(
  medplum: MedplumClient,
  practitionerId: string,
  panelId: string,
  vendor?: string,
): Promise<CustomTestPanel[]> {
  const practitioner = await medplum.readResource('Practitioner', practitionerId)
  const allPanels = loadCustomPanels(medplum, practitioner, vendor)
  const updated = allPanels.filter((p) => p.id !== panelId)
  await savePanels(medplum, practitioner, updated, vendor)
  return vendor ? updated.filter((p) => !p.vendor || p.vendor === vendor) : updated
}
